import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import type { HeaderCarousel } from 'types/capi/modules';

import ChevronIcon from '~/assets/icons/Chevron';

import { Analytics, Variant } from '.';
import Slide from './Slide';

type Props = {
  analytics: Analytics;
  module: HeaderCarousel;
  variant: Variant;
};

const AUTO_SLIDE_DURATION = 5000;

export default function Carousel({ module, variant, analytics }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    dragFree: false,
    loop: true,
    slidesToScroll: 1
  });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [timerStarted, setTimerStarted] = useState(false);
  const { items: slides } = module;

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    const selectedSlide = emblaApi.selectedScrollSnap();

    setTimerStarted(false);
    setCurrentSlideIndex(selectedSlide);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const timer = setTimeout(() => {
      emblaApi.scrollNext();
      setTimerStarted(false);
    }, AUTO_SLIDE_DURATION);

    // React bug work-around
    const delay = setTimeout(() => {
      setTimerStarted(true);
    }, 50);

    return () => {
      clearTimeout(timer);
      clearTimeout(delay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, currentSlideIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative overflow-hidden">
      <div className="absolute top-2 z-10 flex w-full items-center justify-center px-4 md:bottom-4 md:top-auto">
        <div className="flex w-full gap-2 md:max-w-md">
          {slides.map((_, i) => (
            <span
              key={`nav-btn-${i}`}
              className="flex h-1 w-full justify-start overflow-hidden rounded-full bg-[rgba(255,255,255,0.5)]"
            >
              <span
                className={`w-full origin-left rounded-full bg-white ease-linear`}
                style={{
                  transform: `scaleX(${i < currentSlideIndex ? 100 : i === currentSlideIndex && timerStarted ? 100 : 0}%)`,
                  transitionDuration: `${i === currentSlideIndex && timerStarted ? `${AUTO_SLIDE_DURATION}ms` : '0s'}`,
                  transitionProperty: `${i === currentSlideIndex && timerStarted ? 'transform' : 'none'}`
                }}
              ></span>
            </span>
          ))}
        </div>
      </div>

      <div className="absolute inset-y-1/2 z-10 hidden w-full items-center justify-between px-8 md:flex">
        <button
          onClick={() => emblaApi?.scrollPrev()}
          className="rotate-90 rounded-full bg-black bg-opacity-[16%] p-[10px]"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
        <button
          onClick={() => emblaApi?.scrollNext()}
          className="rotate-270 rounded-full bg-black bg-opacity-[16%] p-[10px]"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
      </div>

      <div ref={emblaRef}>
        <div className="flex">
          {slides.map((slide, i) => (
            <Slide
              variant={variant}
              analytics={analytics}
              key={`carousel-slide-${i}`}
              slide={slide}
              index={i}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
